/**
 * `/connectors` — the attorney's connected apps. Every connection here is
 * personal (it follows the signed-in user, not the firm) and is what the case,
 * client and practice agents use when they act on that attorney's behalf.
 */
import { Plug, RefreshCw } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { ListCard } from "@/components/ui/list-card";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";

import { ConnectorGroup, groupByCategory } from "../components/ConnectorGroup";
import { useConnectors } from "../hooks/useConnectors";

/** The list's shape while it loads: two sections of rows. */
const SkeletonList: FC = () => (
  <div className="flex flex-col gap-8" data-testid="connectors-loading">
    {[3, 4].map((count, i) => (
      <div key={i} className="flex flex-col gap-3">
        <Skeleton className="h-3.5 w-36" />
        <ListCard>
          {Array.from({ length: count }, (_, j) => (
            <div key={j} className="flex items-center gap-3 px-4 py-3">
              <Skeleton className="size-8 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-2.5 w-2/3" />
              </div>
              <Skeleton className="h-7 w-20" />
            </div>
          ))}
        </ListCard>
      </div>
    ))}
  </div>
);

export const ConnectorsPage: FC = () => {
  const {
    items,
    enabled,
    loading,
    error,
    pendingSlug,
    disconnectingSlug,
    refresh,
    connect,
    disconnect
  } = useConnectors();

  const connected = items.filter((c) => c.status === "connected").length;
  const groups = groupByCategory(items);

  return (
    <div
      className="h-full min-h-0 w-full overflow-y-auto bg-surface"
      data-testid="connectors-page"
    >
      <div className="mx-auto box-content flex max-w-190 flex-col gap-8 px-12 pt-10 pb-24">
        <PageHeader
          title="Connectors"
          description="Apps your agents can use on your behalf. Connections are personal to you and work in every agent."
          actions={
            !loading && enabled ? (
              <>
                <span
                  className="text-sm text-fg-3 tabular-nums"
                  data-testid="connectors-summary"
                >
                  {connected} of {items.length} connected
                </span>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={() => void refresh()}
                  aria-label="Refresh"
                >
                  <RefreshCw />
                </Button>
              </>
            ) : null
          }
        />

        {loading ? (
          <SkeletonList />
        ) : !enabled ? (
          <EmptyState
            icon={Plug}
            title="Connectors Aren't Set Up Yet"
            description="This workspace isn't configured for connected apps. Contact CaseBlink support to turn them on."
            className="py-16"
            data-testid="connectors-disabled"
          />
        ) : error && items.length === 0 ? (
          <EmptyState
            icon={Plug}
            title="Couldn't Load Your Connectors"
            description={error}
            action={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void refresh()}
              >
                Try Again
              </Button>
            }
            className="py-16"
            data-testid="connectors-error"
          />
        ) : (
          <div className="flex flex-col gap-8" data-testid="connectors-grid">
            {groups.map((group) => (
              <ConnectorGroup
                key={group.category}
                category={group.category}
                connectors={group.connectors}
                pendingSlug={pendingSlug}
                disconnectingSlug={disconnectingSlug}
                onConnect={(slug) => void connect(slug)}
                onDisconnect={(slug) => void disconnect(slug)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
